import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './lib/auth';
import { AppLayout } from './components/AppLayout';
import { PageLoader } from './components/ui';
import type { Permission } from '../shared/permissions';

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const AcceptInvitePage = lazy(() => import('./pages/auth/AcceptInvitePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TemplatesPage = lazy(() => import('./pages/templates/TemplatesPage'));
const TemplateDetailPage = lazy(() => import('./pages/templates/TemplateDetailPage'));
const TemplateBuilderPage = lazy(() => import('./pages/templates/TemplateBuilderPage'));
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage'));
const DocumentPage = lazy(() => import('./pages/documents/DocumentPage'));
const FillPage = lazy(() => import('./pages/documents/FillPage'));
const ReviewsPage = lazy(() => import('./pages/documents/ReviewsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const AccountPage = lazy(() => import('./pages/account/AccountPage'));
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const AuditPage = lazy(() => import('./pages/admin/AuditPage'));
const JobsPage = lazy(() => import('./pages/admin/JobsPage'));
const OrganisationSettingsPage = lazy(() => import('./pages/admin/OrganisationSettingsPage'));
const OrganisationsPage = lazy(() => import('./pages/admin/OrganisationsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <PageLoader />;
  if (status === 'anonymous') {
    const next = location.pathname + location.search;
    return <Navigate to={`/login${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }
  return <>{children}</>;
}

function RequirePerm({ perms, any, children }: { perms: Permission[]; any?: boolean; children: ReactNode }) {
  const { can, canAny } = useAuth();
  const ok = any ? canAny(...perms) : can(...perms);
  return ok ? <>{children}</> : <NotFoundPage />;
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <PageLoader />;
  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/'} replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
          <Route path="/register" element={<GuestOnly><RegisterPage /></GuestOnly>} />
          <Route path="/forgot-password" element={<GuestOnly><ForgotPasswordPage /></GuestOnly>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          {/* Full-screen editors */}
          <Route
            path="/templates/:id/builder/:versionId"
            element={<RequireAuth><RequirePerm perms={['templates.edit']}><TemplateBuilderPage /></RequirePerm></RequireAuth>}
          />
          <Route path="/documents/:id/fill" element={<RequireAuth><FillPage /></RequireAuth>} />

          <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
            <Route index element={<DashboardPage />} />
            <Route path="templates" element={<RequirePerm perms={['templates.view']}><TemplatesPage /></RequirePerm>} />
            <Route path="templates/:id" element={<RequirePerm perms={['templates.view']}><TemplateDetailPage /></RequirePerm>} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="documents/:id" element={<DocumentPage />} />
            <Route path="reviews" element={<RequirePerm perms={['documents.review']}><ReviewsPage /></RequirePerm>} />
            <Route path="customers" element={<RequirePerm perms={['customers.view']}><CustomersPage /></RequirePerm>} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="admin" element={<RequirePerm perms={['admin.dashboard']}><AdminOverviewPage /></RequirePerm>} />
            <Route path="admin/users" element={<RequirePerm perms={['users.manage']}><UsersPage /></RequirePerm>} />
            <Route path="admin/roles" element={<RequirePerm perms={['users.manage']}><RolesPage /></RequirePerm>} />
            <Route path="admin/audit" element={<RequirePerm perms={['audit.view']}><AuditPage /></RequirePerm>} />
            <Route path="admin/jobs" element={<RequirePerm perms={['org.manage', 'system.manage']} any><JobsPage /></RequirePerm>} />
            <Route path="admin/organisation" element={<RequirePerm perms={['org.manage']}><OrganisationSettingsPage /></RequirePerm>} />
            <Route path="admin/organisations" element={<RequirePerm perms={['system.manage']}><OrganisationsPage /></RequirePerm>} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
